/**
 * @fileoverview Mapbox Harita Instance Hook'u
 *
 * Bu modul, Mapbox GL JS harita instance'ini olusturmak ve yonetmek
 * icin kullanilan custom hook'u icerir. Harita yaratma, stil yonetimi,
 * kullanici etkilesimleri ve sinirlari (bounds) takip etme islevlerini saglar.
 *
 * Hook, React lifecycle ile Mapbox harita yasam dongusunu senkronize eder.
 *
 * @module useMapInstance
 * @requires mapbox-gl
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/} Mapbox GL JS
 */

import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { computeValidCoords, fitMapToMarkers, DEFAULT_MAP_CENTER, MAP_ZOOM, TURKEY_BOUNDS } from "../utils";

/**
 * @typedef {Object} MapBounds
 * @property {string} bottom_left - Guneybati koordinati (lat,lng formatinda)
 * @property {string} top_right - Kuzeydogu koordinati (lat,lng formatinda)
 */

/**
 * @typedef {Object} UseMapInstanceOptions
 * @property {React.RefObject<HTMLDivElement>} mapRef - Harita konteyner elementi ref'i
 * @property {Array<Object>} listings - Koordinat iceren ilan nesneleri dizisi
 * @property {boolean} isDark - Karanlik mod etkin mi
 * @property {Function} [onMapMove] - Kullanici surukleme/zoom baslattiginda callback
 * @property {Function} [onBoundsChange] - Harita sinirlari degistiginde callback
 */

/**
 * @typedef {Object} UseMapInstanceReturn
 * @property {mapboxgl.Map|null} map - Mapbox harita instance'i
 * @property {boolean} mapReady - Harita yuklendi mi
 */

/**
 * Mapbox harita instance'ini olusturur ve yonetir.
 *
 * Bu hook, Mapbox GL JS haritasinin tum yasam dongusunu yonetir:
 * - Harita olusturma ve yapilandirma
 * - Karanlik/aydinlik tema gecisi
 * - Turkiye sinirlari icinde kisitlama (maxBounds)
 * - Kullanici etkilesimleri (drag, zoom) takibi
 * - Bounds degisikligi bildirimi
 * - Pencere yeniden boyutlandirma islemleri
 * - Cleanup (temizlik) islemleri
 *
 * **Harita Yapilandirmasi:**
 * - Stil: dark-v11 veya streets-v12 (tema'ya gore)
 * - Projeksiyon: Mercator
 * - Min/Max Zoom: MAP_ZOOM.MIN / MAP_ZOOM.MAX
 * - Max Bounds: TURKEY_BOUNDS (Turkiye sinirlari)
 *
 * **Onemli:** Hook bir kez harita olusturur ve unmount'ta temizler.
 * isDark degistiginde harita yeniden olusturulur.
 *
 * @function useMapInstance
 * @param {UseMapInstanceOptions} options - Hook opsiyonlari
 * @returns {UseMapInstanceReturn} Harita instance ve hazir durumu
 *
 * @example
 * const mapContainerRef = useRef(null);
 * const { map, mapReady } = useMapInstance({
 *   mapRef: mapContainerRef,
 *   listings: listings,
 *   isDark: theme === "dark",
 *   onMapMove: () => console.log("Kullanici haritayi hareket ettiriyor"),
 *   onBoundsChange: (bounds) => searchInBounds(bounds),
 * });
 *
 * return <div ref={mapContainerRef} style={{ height: "100%" }} />;
 */
export function useMapInstance({ mapRef, listings, isDark, onMapMove = null, onBoundsChange = null }) {
  const mapObj = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const listingsRef = useRef(listings);
  const onMapMoveRef = useRef(onMapMove);
  const onBoundsChangeRef = useRef(onBoundsChange);

  // Keep refs updated
  useEffect(() => {
    listingsRef.current = listings;
  }, [listings]);

  useEffect(() => {
    onMapMoveRef.current = onMapMove;
  }, [onMapMove]);

  useEffect(() => {
    onBoundsChangeRef.current = onBoundsChange;
  }, [onBoundsChange]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;

    const initialCoords = computeValidCoords(listingsRef.current);

    const map = new mapboxgl.Map({
      container: mapRef.current,
      style: isDark
        ? "mapbox://styles/mapbox/dark-v11"
        : "mapbox://styles/mapbox/streets-v12",
      center: initialCoords.length ? initialCoords[0] : DEFAULT_MAP_CENTER,
      zoom: initialCoords.length ? MAP_ZOOM.LISTING : MAP_ZOOM.DEFAULT,
      minZoom: MAP_ZOOM.MIN,
      maxZoom: MAP_ZOOM.MAX,
      maxBounds: TURKEY_BOUNDS,
      attributionControl: false,
      projection: "mercator",
    });

    map.on("load", () => {
      setMapReady(true);
      map.resize();
      const currentCoords = computeValidCoords(listingsRef.current);
      if (currentCoords.length > 0) {
        setTimeout(() => fitMapToMarkers(map, currentCoords), 100);
      }
    });

    // Notify on map interaction (start)
    map.on("dragstart", () => onMapMoveRef.current?.());
    map.on("zoomstart", () => onMapMoveRef.current?.());

    // Notify when map movement ends with new bounds
    map.on("moveend", () => {
      if (onBoundsChangeRef.current) {
        const bounds = map.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const formatted = {
          bottom_left: `${sw.lat.toFixed(6)},${sw.lng.toFixed(6)}`,
          top_right: `${ne.lat.toFixed(6)},${ne.lng.toFixed(6)}`,
        };
        onBoundsChangeRef.current(formatted);
      }
    });

    mapObj.current = map;

    const handleResize = () => map.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      map.remove();
      mapObj.current = null;
    };
  }, [isDark, mapRef]);

  return { map: mapObj.current, mapReady };
}

export default useMapInstance;
